<style lang="stylus">
.sticky-section {
  top: 10px !important;
  position: sticky !important;
}
</style>

<template>
  <v-container class="container">
    <div>
      <v-row no-gutters class="mt-12">
        <div class="mobile-anchor-border d-flex d-sm-none" />
        <v-col cols="12" md="8" lg="8" xl="8">
          <!-- Course description for what the user is paying for -->
          <v-row row wrap style="margin-bottom: 13px">
            <v-col cols="12" md="11" lg="11" xl="11" style="margin-bottom: 8px">
              <div class="courseLabel left-border">
                <strong>{{ pageTitle }}</strong>
              </div>
            </v-col>
          </v-row>

          <!-- Stripe Checkout -->
          <div class="pa-4">
            <p class="text-h6 mb-4">
              <strong>Course: {{ title }}</strong>
            </p>
            <stripe-checkout
              v-if="isStripePaymentEnabled && amount > 0"
              ref="stripeCheckout"
              :amount="amount"
              :invoice-number="invoiceNumber"
              currency="USD"
              :course-title="(coursePrefixDisplayLabel || coursePrefix) + ' ' + title"
              :course-name="title"
              :course-prefix="coursePrefix"
              :course-start-date="courseStartDate"
              :interest-track="interestTrack"
              :session-name="sessionName"
              :course-type="courseType"
              :course-info="courseInfo"
              :enrollment-id="enrollmentId"
              :offering-id="courseOfferingId"
              :canvas-course-id="canvasCourseId"
              :enrollment-mode="buttonAction === 'Add to Transcript' ? 'credit' : 'verified'"
              :enrollment-type="multiEnrollmentType"
              :promo-code="aetherPromoCode"
              @payment-authorized="handleStripePaymentAuthorized"
              @payment-completed="onStripePaymentCompleted"
              @payment-cancelled="handleStripePaymentCancelled"
              @amount-resolved="onStripeAmountResolved"
            />
            <!-- Legacy/PayPal flow — show enrollment instructions matching CourseCheckout.vue -->
            <div
              v-else-if="
                !isStripePaymentEnabled && ['Upgrade', 'Access Course', 'Enroll and pay'].includes(buttonLabel)
              "
              class="pa-4"
            >
              <p style="font-size: 18px; font-weight: bold; margin-bottom: 0px; color: #2a2a2a">
                Complete the enrollment process
              </p>
              <ol class="eligible-list mt-2" style="padding-left: 20px">
                <li>Pay the course enrollment fee.</li>
                <li>Verify your identity (This process only needs to be completed one time).</li>
                <li>
                  Once completed, you will be able to access your course starting on the course start date indicated on
                  your dashboard.
                </li>
              </ol>
            </div>
            <!-- Legacy/PayPal flow — Add to Transcript / Retry for Credit content -->
            <div
              v-else-if="!isStripePaymentEnabled && ['Add to Transcript', 'Retry for Credit'].includes(buttonLabel)"
              class="pa-4"
            >
              <p style="font-size: 16px; font-weight: normal; margin-bottom: 12px">
                Before you get your credit, check our
                <a
                  class="gpa-calc"
                  href="https://ea.asu.edu/how-it-works/gpa-calculator/"
                  target="_blank"
                  @click="asuoDataLayerClick('courseCheckout', '0')"
                  >GPA Calculator</a
                >
                to determine how converting this course will impact your overall GPA.
              </p>
              <div
                style="
                  padding: 8px 12px;
                  border-radius: 18px;
                  border: 1px solid rgb(254, 198, 39);
                  display: flex;
                  align-items: flex-start;
                  gap: 12px;
                "
              >
                <v-icon style="color: rgb(254, 198, 39); margin-top: 12px">mdi-alert</v-icon>
                <div style="padding: 12px 0">
                  <p style="margin-bottom: 10px">
                    <strong><b>Be advised that you can only transcript a course ONCE</b></strong>
                  </p>
                  <p style="font-size: 16px; font-weight: normal; margin-bottom: 0">
                    Once you transcript this course, you will no longer have the option to retake it for a different
                    grade.
                  </p>
                </div>
              </div>
              <p style="font-weight: 600; margin: 20px 0 10px">To get credit:</p>
              <ol style="font-size: 16px; font-weight: 400; margin-bottom: 10px; padding-left: 20px">
                <li>Click the checkout button.</li>
                <li>Enter your payment information, if applicable.</li>
                <li>
                  Remain on the credit conversion page until you see the Credits Converted Successfully confirmation.
                </li>
              </ol>
              <p style="font-size: 16px; font-weight: normal; margin-bottom: 12px">
                After Arizona State University approves your credit you can request an official transcript from ASU.
              </p>
            </div>
            <!-- Free/prepaid Stripe enrollment — no payment needed -->
            <div v-else class="text-center pa-8">
              <p class="text-body-1 mb-2">No payment required for this course.</p>
              <p class="text-body-2 text-grey">
                Click <strong>Continue to payment</strong> to complete your enrollment.
              </p>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="4" lg="4" xl="3">
          <v-col cols="12" sm="12" md="12" xl="12" class="pa-0">
            <v-row row>
              <v-col cols="12" sm="12" md="12">
                <div class="sticky-section">
                  <checkout-selected-course
                    ref="checkoutSelectedCourse"
                    v-scroll="onScroll"
                    @next="next($event)"
                    @payment-processing="handlePaymentProcessing"
                    @payment-complete="handlePaymentComplete"
                    @promo-validated="handlePromoValidated"
                    @promo-removed="handlePromoRemoved"
                  />
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { defineComponent, defineAsyncComponent, h, resolveComponent } from 'vue';
import * as Sentry from '@sentry/vue';
import { reloadingAsyncLoader } from '@/utils/async-component-retry';
import { useCheckoutStore } from '@/stores/checkout';
import { useUserStore } from '@/stores/user';
import { useCoursesStore } from '@/stores/courses';
import { buildGa4EcommerceItem } from '@/utils/ga4Items';
import { getAttributionParams } from '@/util/param-persistence';
import { useStripeCheckout } from '@/composables/checkout/useStripeCheckout';

/* eslint-disable no-console */
import { DataLayerMixin } from '../mixins/dataLayerMixin';
import { AjaxLoader } from '../mixins/ajaxLoader';
import { SessionInitializer } from '../mixins/sessionInitializer';
import { Helper } from '../mixins/helpers.js';
import { Logger } from '../mixins/logger';

// Terminal state after the chunk retry/reload cycle gives up: a learner here
// already had one automatic reload not fix it, so alongside the retry action
// point them at support (same partner-dependent contact URLs as the sidebar).
const StripeCheckoutLoadError = defineComponent({
  name: 'StripeCheckoutLoadError',
  render() {
    const supportUrl = Helper.methods.isUlcPartner()
      ? 'https://ulc.asu.edu/contact-us/'
      : 'https://ea.asu.edu/contact/';
    return h('div', { class: 'pa-4 text-center' }, [
      h('p', { class: 'text-body-1 mb-2' }, "We couldn't load the payment form."),
      h('p', { class: 'text-body-2 mb-4' }, [
        'Please refresh the page to try again, or ',
        h('a', { href: supportUrl, target: '_blank', rel: 'noopener noreferrer' }, 'contact our support team'),
        ' if the problem continues. You have not been charged.'
      ]),
      h(resolveComponent('v-btn'), { color: 'primary', onClick: () => window.location.reload() }, () => 'Refresh page')
    ]);
  }
});

export default defineComponent({
  name: 'courseCheckoutStripe',

  components: {
    'stripe-checkout': defineAsyncComponent({
      ...reloadingAsyncLoader(() => import('../components/StripeCheckout.vue'), {
        name: 'stripe-checkout'
      }),
      errorComponent: StripeCheckoutLoadError
    })
  },

  data() {
    return {
      reachedBottom: false,
      checkoutStore: null
    };
  },

  setup() {
    const {
      stripePaymentCompleted,
      aetherPromoCode,
      handleStripePaymentAuthorized,
      handleStripePaymentCompleted,
      handleStripePaymentCancelled,
      handlePromoValidated,
      handlePromoRemoved,
      clearStripeCart,
      generateInvoiceNumber,
      triggerStripePayment
    } = useStripeCheckout();

    return {
      stripePaymentCompleted,
      aetherPromoCode,
      handleStripePaymentAuthorized,
      handleStripePaymentCompleted,
      handleStripePaymentCancelled,
      handlePromoValidated,
      handlePromoRemoved,
      clearStripeCart,
      generateInvoiceNumber,
      triggerStripePayment
    };
  },

  created() {
    // Get checkout store instance
    this.checkoutStore = useCheckoutStore();

    // Redirect to dashboard if no checkout data in store
    if (!this.checkoutStore.hasValidCheckoutData) {
      this.$router.push({ name: 'dashboard' });
      return;
    }

    // Redirect to dashboard if required course details are missing
    if (!(this.coursePrefix && this.title)) {
      this.$router.push({ name: 'dashboard' });
      return;
    }

    this.captureEcomDataLayer('view_cart');
  },

  computed: {
    pageTitle() {
      return this.checkoutStore?.pageTitle || '';
    },
    title() {
      return this.checkoutStore?.title || '';
    },
    coursePrefix() {
      return this.checkoutStore?.coursePrefix || '';
    },
    coursePrefixDisplayLabel() {
      return this.checkoutStore?.coursePrefixDisplayLabel || '';
    },
    courseStartDate() {
      return this.checkoutStore?.courseStartDate || '';
    },
    courseType() {
      return this.checkoutStore?.courseType || '';
    },
    interestTrack() {
      return this.checkoutStore?.interestTrack || '';
    },
    sessionName() {
      return this.checkoutStore?.sessionName || '';
    },
    courseOfferingName() {
      return this.checkoutStore?.courseOfferingName || '';
    },
    currentGrade() {
      return this.checkoutStore?.currentGrade || '';
    },
    grade() {
      return this.checkoutStore?.grade || 0;
    },
    courseInfo() {
      const info = this.checkoutStore?.courseInfo || {};
      return info;
    },
    buttonLabel() {
      return this.checkoutStore?.buttonLabel || '';
    },
    benefitType() {
      return this.checkoutStore?.benefitType || '';
    },
    finalGrade() {
      return this.checkoutStore?.finalGrade || '';
    },
    buttonAction() {
      return this.checkoutStore?.buttonAction || '';
    },
    courseCompletedDate() {
      return this.checkoutStore?.courseCompletedDate || '';
    },
    courseOfferingId() {
      return this.checkoutStore?.courseOfferingId || '';
    },
    enrollmentId() {
      return this.checkoutStore?.enrollmentId || '';
    },
    creditConversionPrice() {
      return this.checkoutStore?.creditConversionPrice || 0;
    },
    creditEligibilityPrice() {
      return this.checkoutStore?.creditEligibilityPrice || 0;
    },
    canvasEnrollmentId() {
      return this.checkoutStore?.canvasEnrollmentId || null;
    },
    canvasUserId() {
      return this.checkoutStore?.canvasUserId || null;
    },
    canvasCourseId() {
      return this.checkoutStore?.canvasCourseId || null;
    },
    credits() {
      return this.checkoutStore?.credits || 0;
    },
    enrollmentDate() {
      return this.checkoutStore?.enrollmentDate || '';
    },
    from() {
      return this.checkoutStore?.from || '';
    },
    lms() {
      return this.checkoutStore?.lms || '';
    },
    benefitsEligible() {
      return this.checkoutStore?.benefitsEligible || false;
    },
    enrollmentType() {
      return this.checkoutStore?.enrollmentType || '';
    },
    multiEnrollmentType() {
      return this.checkoutStore?.multiEnrollmentType || '';
    },
    invoiceNumber() {
      return this.checkoutStore?.invoiceNumber || this.generateInvoiceNumber();
    },
    isPrepaidCourseAmount() {
      // BT5 'Both' partner courses can resolve to either PartnerPay or LearnerPay
      // per partner_and_degree. Only treat as prepaid (skip Stripe) when the
      // resolved type is partner-pay; LearnerPay must still go through Stripe.
      // Matches the logic in CheckoutSelectedCourse.vue (PayPal flow).
      const coursesStore = useCoursesStore();
      if (coursesStore.enrolledCourses) {
        return (
          (this.buttonAction === 'Add to Transcript' && ['1', '3', '5'].includes(this.benefitType)) ||
          (['UpgradeModal', 'Upgrade', 'Enroll and pay'].includes(this.buttonAction) &&
            ['1', '2', '5'].includes(this.benefitType) &&
            this.multiEnrollmentType !== 'LearnerPay')
        );
      }
      return false;
    },
    amount() {
      if (this.isPrepaidCourseAmount) return 0;
      return this.checkoutStore?.amount || this.creditEligibilityPrice || this.creditConversionPrice || 0;
    },
    isStripePaymentEnabled() {
      const userStore = useUserStore();
      return userStore.apis?.learnerData?.useAetherFlow || false;
    }
  },

  mixins: [AjaxLoader, DataLayerMixin, SessionInitializer, Helper, Logger],

  methods: {
    captureEcomDataLayer(event) {
      // Clear previous ecommerce object
      if (window.dataLayer) {
        window.dataLayer.push({ ecommerce: null });
      }

      const userStore = useUserStore();
      const leSfContactId = userStore.apis?.learnerData?._contactId || userStore.apis?.learnerData?.contactId || '';
      const { campaign, partner } = getAttributionParams();

      //Ecom data layer
      const ecom = {
        event: event,
        le_sf_contact_id: leSfContactId,
        campaign,
        partner,
        ecommerce: {
          currency: 'USD',
          value: 25.0,
          ...(event === 'purchase' && {
            transaction_id: this.transactionID
          }),
          items: [
            buildGa4EcommerceItem({
              canvasCourseId: this.canvasCourseId,
              offeringId: this.courseOfferingId,
              courseName: this.title,
              coursePrefix: this.coursePrefix,
              interestTrack: this.interestTrack,
              sessionName: this.sessionName,
              startDate: this.courseStartDate,
              courseType: this.courseType,
              price: 25.0
            })
          ]
        }
      };

      this.asuoDataLayerClick('ecommerceTags', '0', ecom);
    },

    handlePaymentProcessing() {
      // Show page-wide spinner when payment is processing
    },

    handlePaymentComplete() {
      // Clear page-wide spinner when payment processing completes
    },

    onStripePaymentCompleted(event) {
      this.handleStripePaymentCompleted(event, {
        checkoutSelectedCourseRef: this.$refs.checkoutSelectedCourse,
        invoiceNumber: this.invoiceNumber,
        amount: this.amount,
        captureEcomDataLayer: this.captureEcomDataLayer
      });
    },

    onStripeAmountResolved({ amount }) {
      if (typeof amount !== 'number' || amount < 0) return;
      // Aether computed the canonical price (e.g. BT5 consolidated). Override the
      // legacy `creditEligibilityPrice`/`creditConversionPrice` so the sidebar and
      // downstream receipt screen reflect what Stripe is actually charging.
      if (this.checkoutStore) {
        this.checkoutStore.amount = amount;
      }
      // CheckoutSelectedCourse.vue (the sidebar) keeps its own local `amount`
      // data field (initialized from creditEligibilityPrice/creditConversionPrice),
      // so the store update alone doesn't reach it. Push the canonical amount
      // into the child's local state so the displayed total matches Stripe.
      const sidebar = this.$refs.checkoutSelectedCourse;
      if (sidebar) {
        sidebar.amount = String(amount);
        if (Array.isArray(sidebar.courseCheckoutItems) && sidebar.courseCheckoutItems[0]) {
          sidebar.courseCheckoutItems[0].price = String(amount);
        }
      }
    },

    next(event) {
      this.logInfo('courseCheckoutStripe', event);

      // If Stripe payment was explicitly requested, trigger Stripe confirm
      if (event?.paymentMethod === 'stripe') {
        // After a failed chunk load the ref can point at the errorComponent,
        // which has no payment API — require the real form, not just a truthy ref.
        const stripeCheckout = this.$refs.stripeCheckout;
        if (stripeCheckout && typeof stripeCheckout.handlePayment === 'function') {
          this.triggerStripePayment(stripeCheckout);
          return;
        }
        // No mounted payment form means nothing was charged or enrolled — surface an error, never a receipt.
        this.logError(
          'Stripe payment requested but the payment form is not mounted; refusing to build a receipt',
          new Error('STRIPE_CHECKOUT_NOT_MOUNTED')
        );
        Sentry.captureMessage('Stripe payment requested but the payment form is not mounted', {
          level: 'error',
          tags: { source: 'stripe_checkout', phase: 'form_not_mounted' },
          extra: { amount: this.amount, enrollmentId: this.checkoutStore?.enrollmentId }
        });
        const sidebar = this.$refs.checkoutSelectedCourse;
        if (sidebar) {
          sidebar.errorMsg =
            'We were unable to start your payment. Please try again, or contact support if the problem persists.';
          sidebar.showPaymentError = true;
        }
        return;
      }

      // Payment processing spinner is now handled by payment events
      // Start loading for transaction completion

      // Handle payment with payment method and transactions
      // Receipt dates reflect the EMVD (Enrollment Mode Verified Date) — when the
      // learner completed enrollment — not the course offering's start date.
      const enrollmentStartDate = new Date();
      const enrollmentEndDate = new Date(enrollmentStartDate);
      enrollmentEndDate.setFullYear(enrollmentStartDate.getFullYear() + 1);

      let transactionData = {
        courseCode: this.coursePrefix,
        courseTitle: this.title,
        coursePrefixDisplayLabel: this.coursePrefixDisplayLabel,
        startDate: enrollmentStartDate.toLocaleDateString(),
        endDate: enrollmentEndDate.toLocaleDateString(),
        courseOfferingId: this.courseOfferingId,
        canvasCourseId: this.canvasCourseId,
        courseStartDate: this.courseStartDate,
        interestTrack: this.interestTrack,
        sessionName: this.sessionName,
        courseType: this.courseType,
        credits: this.credits || '3',
        buttonAction: this.buttonAction,
        finalGrade: this.finalGrade,
        enrollmentId: this.enrollmentId,
        from: this.from
      };

      if ((event.payer || {}).payment_method && event.transactions[0]) {
        transactionData.transactionId = event.id;
        transactionData.orderNumber = event.transactions[0].invoice_number;
        transactionData.totalAmount = event.transactions[0].amount.total;
        transactionData.orderDate = new Date().toLocaleDateString();
      } else if (event.paymentMode === 'free') {
        // Handle voucher-based free transactions
        transactionData.transactionId = event.id || 'free' + Math.random().toString(36).substring(2, 15);
        transactionData.orderNumber =
          (event.transactions && event.transactions[0]?.invoice_number) || `free-` + this.generateInvoiceNumber();
        transactionData.totalAmount = event.transactions[0].amount.total;
        transactionData.orderDate = new Date().toLocaleDateString();
      } else {
        // Handle benefits eligible transactions
        transactionData.totalAmount = 0;
        transactionData.transactionId = 'BE-' + Date.now();
        transactionData.orderNumber = 'BE-' + this.generateInvoiceNumber();
        transactionData.orderDate = new Date().toLocaleDateString();
      }

      // Store transaction data in session storage
      sessionStorage.setItem('checkoutTransactionData', JSON.stringify(transactionData));

      // Navigate to completion page (data stored in sessionStorage)
      this.$router.push({
        name: 'courseCheckoutCompletion'
      });

      // NOTE for devs (ULC-2946 / Kyle Rassel Apr 2026):
      // Do NOT call captureEcomDataLayer('purchase') here.
      // In a Vue SPA, router.push() changes the URL synchronously, so GTM would record
      // this event against the completion page URL — before CourseCheckoutCompletion.vue
      // fires its own authoritative purchase event (with a real transaction_id from
      // sessionStorage). That bogus event exhausts GTM's "once per page" budget and
      // suppresses the real one. The authoritative purchase push lives in
      // CourseCheckoutCompletion.vue mounted() — leave it there.
      // Same fix was applied to CourseCheckout.vue (PayPal path) in commit 74d348fd.
    },

    onScroll() {
      if (window.innerHeight + window.scrollY >= document.documentElement.offsetHeight - 250) {
        this.reachedBottom = true;
      } else {
        this.reachedBottom = false;
      }
    }
  },

  mounted() {
    // Send checkout page view event
    const checkoutPageEvent = {
      event: 'page_view',
      page_type: 'checkout',
      page_path: this.$route.path,
      course_info: {
        course_prefix: this.coursePrefix,
        course_title: this.title,
        course_full_name: `${this.coursePrefix} ${this.title}`
      }
    };

    if (window.dataLayer) {
      window.dataLayer.push(checkoutPageEvent);
    }

    const bannerElement = document.getElementById('banner-12');
    if (bannerElement) {
      bannerElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      });
    }
  },

  beforeUnmount() {
    // Clear Stripe cart if user navigates away without completing payment
    if (!this.stripePaymentCompleted) {
      this.clearStripeCart(this.courseOfferingId);
    }
  }
});
</script>
